import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { User, LogOut, Settings, Sliders, Volume2, Sparkles, Target, Shield, School, Edit2, Save, X, Link as LinkIcon, Copy, CheckCircle2, Eye, Heart, GraduationCap, Camera, Trash2, Calendar, AlertCircle, Users } from 'lucide-react';
import { Toggle } from '../ui/Toggle';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { AppState } from '../../app/types';
import { GroupId, StartingPeriod } from '../../config/systemConfig';
import { type AccountsState, isLearnerAccount, isTeacherAccount } from '../../accounts';
import { getAccountRoleLabel } from '../../accounts/logic/uiHelpers';
import { PageContainer } from '../shared/PageContainer';
import { PageHero } from '../shared/PageHero';
import { WorldZone } from '../shared/WorldZone';
import { itemVariants } from '../shared/animations';
import { useDjaySelection } from '../../mascot';
import { dataLayer, type LearnerPreferences } from '../../data/dataLayer';
import { useAppStore } from '../../app/hooks/useAppStore';
import { getProgressToNextLevel } from '../../config/constants';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { ProgressBar } from '../ui/Progress';
import { soundManager } from '../../utils/soundManager';
import { getAutoPhaseLabel } from '../../mastery';
const DEFAULT_PREFS: LearnerPreferences = {
  sound: true,
  reducedMotion: false,
  dailyGoal: 10
};
interface SettingsPageProps {
  state: AppState;
  selectedGroup: GroupId;
  startingPeriod: StartingPeriod;
  manualPeriod?: StartingPeriod | null;
  onSetStartingPeriod: (period: StartingPeriod | null) => void;
  onUpdateName?: (newName: string) => void;
  accounts?: AccountsState;
  onLogout?: () => void;
  viewingLearnerId?: string | null;
}
export function SettingsPage({
  state,
  selectedGroup,
  startingPeriod,
  manualPeriod,
  onSetStartingPeriod,
  onUpdateName,
  accounts,
  onLogout,
  viewingLearnerId
}: SettingsPageProps) {
  const {
    resetLearnerProgress,
    joinClassroomByCode
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  // Persisted preferences — loaded async from dataLayer
  const [prefs, setPrefs] = useState<LearnerPreferences>(DEFAULT_PREFS);
  // Load preferences from dataLayer on mount
  useEffect(() => {
    dataLayer.getLearnerPreferences().then(setPrefs).catch(() => {});
  }, []);
  // Save preferences to dataLayer on change
  useEffect(() => {
    dataLayer.saveLearnerPreferences(prefs);
  }, [prefs]);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const confirmResetProgress = () => {
    const learnerId = viewingLearnerId || accounts?.activeAccountId;
    if (learnerId) {
      resetLearnerProgress(learnerId);
      onSetStartingPeriod(null);
    }
    setResetConfirmOpen(false);
  };
  const updatePref = <K extends keyof LearnerPreferences,>(key: K, value: LearnerPreferences[K]) => {
    setPrefs(prev => {
      const newPrefs = {
        ...prev,
        [key]: value
      };
      if (key === 'sound') {
        // Refresh sound manager cache immediately
        setTimeout(() => soundManager.refreshPreference(), 0);
      }
      return newPrefs;
    });
  };
  const activeAccount = accounts ? accounts.accountsById[accounts.activeAccountId] : null;
  const isViewingLearner = !!viewingLearnerId && (activeAccount?.role === 'parent' || activeAccount?.role === 'teacher');
  const displayName = isViewingLearner ? state.stats.name : activeAccount?.displayName || state.stats.name;
  const accountRole = activeAccount?.role || 'learner';
  const roleLabel = getAccountRoleLabel(accountRole);
  const isLearner = accountRole === 'learner';
  // Name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(displayName);
  useEffect(() => {
    setEditedName(displayName);
  }, [displayName]);
  const handleSaveName = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== displayName && onUpdateName) {
      onUpdateName(trimmedName);
      setIsEditingName(false);
    } else {
      setEditedName(displayName);
      setIsEditingName(false);
    }
  };
  const handleCancelEdit = () => {
    setEditedName(displayName);
    setIsEditingName(false);
  };
  // Avatar upload handling
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = event => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_SIZE = 200;
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        updatePref('avatarUrl', dataUrl);
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
    // Reset input so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleRemoveAvatar = () => {
    updatePref('avatarUrl', undefined);
  };
  // Get linked parents and teachers for the current learner
  const linkedInfo = useMemo(() => {
    if (!accounts || accountRole !== 'learner') return null;
    const learnerId = accounts.activeAccountId;
    if (!learnerId) return null;
    const learnerAccount = accounts.accountsById[learnerId];
    if (!learnerAccount || !isLearnerAccount(learnerAccount)) return null;
    // Get linked parents
    const parentIds = accounts.relationships.learnerToParents[learnerId] || [];
    const parents = parentIds.map(id => accounts.accountsById[id]).filter(Boolean).map(a => ({
      name: a.displayName,
      role: 'Ouder' as const
    }));
    // Get linked teachers
    const teachers: {
      name: string;
      role: 'Docent';
      className?: string;
    }[] = [];
    Object.entries(accounts.relationships.teacherToLearners).forEach(([teacherId, learnerIds]) => {
      if (learnerIds.includes(learnerId)) {
        const teacher = accounts.accountsById[teacherId];
        if (teacher && isTeacherAccount(teacher)) {
          const teacherClassroom = Object.values(accounts.classrooms ?? {}).find(c => c.teacherId === teacherId && c.learnerIds.includes(learnerId));
          teachers.push({
            name: teacher.displayName,
            role: 'Docent',
            className: teacherClassroom?.name
          });
        }
      }
    });
    return {
      parents,
      teachers,
      linkCode: learnerAccount.linkCode,
      learnerId
    };
  }, [accounts, accountRole]);
  // Copy link code state
  const [codeCopied, setCodeCopied] = useState(false);
  const handleCopyCode = () => {
    if (linkedInfo?.linkCode) {
      navigator.clipboard.writeText(linkedInfo.linkCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };
  // Join Classroom State
  const [joinCode, setJoinCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinSuccess, setJoinSuccess] = useState(false);
  const handleJoinClassroom = async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 6) {
      setJoinError('Een klascode bestaat uit 6 tekens.');
      return;
    }
    setJoinLoading(true);
    setJoinError(null);
    try {
      const result = await joinClassroomByCode(code);
      if (result.success) {
        setJoinSuccess(true);
        setJoinCode('');
      } else {
        setJoinError(result.error || 'Klas niet gevonden of al lid.');
      }
    } catch (e) {
      setJoinError('Er is een fout opgetreden bij het koppelen.');
    } finally {
      setJoinLoading(false);
    }
  };
  const dailyGoalOptions: {
    value: 5 | 10 | 20;
    emoji: string;
    label: string;
  }[] = [{
    value: 5,
    emoji: '🌱',
    label: 'Rustig'
  }, {
    value: 10,
    emoji: '⭐',
    label: 'Standaard'
  }, {
    value: 20,
    emoji: '🚀',
    label: 'Uitdaging'
  }];
  const weeklyGoalOptions: {
    value: 3 | 5 | 7;
    emoji: string;
    label: string;
  }[] = [{
    value: 3,
    emoji: '🥉',
    label: 'Basis'
  }, {
    value: 5,
    emoji: '🥈',
    label: 'Actief'
  }, {
    value: 7,
    emoji: '🥇',
    label: 'Elke dag'
  }];
  const periodOptions: {
    value: StartingPeriod | null;
    emoji: string | React.ReactNode;
    label: string;
    fullLabel: string;
  }[] = [{
    value: null,
    emoji: <Sparkles className="w-6 h-6 mx-auto text-emerald-400" data-id="element-1905" />,
    label: 'Auto',
    fullLabel: 'Automatisch'
  }, {
    value: 'B',
    emoji: '🌱',
    label: 'Begin',
    fullLabel: 'Begin schooljaar'
  }, {
    value: 'M',
    emoji: '☀️',
    label: 'Midden',
    fullLabel: 'Midden schooljaar'
  }, {
    value: 'E',
    emoji: '🎓',
    label: 'Eind',
    fullLabel: 'Eind schooljaar'
  }];
  const hasMasteryData = Object.keys(state.familyMastery || {}).length > 0;
  return <PageContainer data-id="element-1906">
      <motion.div variants={itemVariants} data-id="element-1907">
        <PageHero title="Instellingen" subtitle="Pas je profiel en voorkeuren aan" gradientClasses="from-orange-500 via-amber-500 to-yellow-500" icon={Settings} iconBgClass="bg-orange-400" djayEmotion="happy" djayVariant={selectedVariant} floatingEmojis={['✨', '🎨']} data-id="element-1908" />
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-1909">
        <WorldZone title="Profiel" icon={User} accentColor="bg-orange-100 text-orange-600" isLast={false} data-id="element-1910">
          <Card className="p-8 md:p-10" data-id="element-1911">
            <div className="flex flex-col sm:flex-row items-center gap-8" data-id="element-1912">
              {/* Avatar */}
              <div className="flex flex-col items-center gap-3" data-id="element-1913">
                <div className="relative flex-shrink-0 cursor-pointer group" onClick={() => fileInputRef.current?.click()} data-id="element-1914">
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleAvatarUpload} data-id="element-1915" />
                  <div className="w-28 h-28 rounded-full flex items-center justify-center text-white text-5xl font-bold shadow-lg shadow-orange-200/50 bg-gradient-to-br from-orange-400 to-amber-500 overflow-hidden transition-transform group-hover:scale-105" data-id="element-1916">
                    {prefs.avatarUrl ? <img src={prefs.avatarUrl} alt="Avatar" className="w-full h-full object-cover" data-id="element-1917" /> : displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-2 shadow-md border border-slate-100 text-slate-400 group-hover:text-orange-500 group-hover:border-orange-200 transition-colors" data-id="element-1918">
                    <Camera className="w-5 h-5" data-id="element-1919" />
                  </div>
                  <div className="absolute -top-2 -left-2 bg-white rounded-full p-1 shadow-sm z-10" data-id="element-1920">
                    <div className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-1921">
                      Lvl {state.stats.level}
                    </div>
                  </div>
                </div>
                {prefs.avatarUrl && <button onClick={handleRemoveAvatar} className="text-xs text-rose-500 hover:text-rose-600 font-medium flex items-center gap-1" data-id="element-1922">
                    <Trash2 className="w-3 h-3" data-id="element-1923" />
                    Verwijder foto
                  </button>}
              </div>

              {/* Info */}
              <div className="flex-1 text-center sm:text-left" data-id="element-1924">
                {/* Name with Edit */}
                {isEditingName ? <div className="flex items-center gap-3 mb-3 justify-center sm:justify-start" data-id="element-1925">
                    <Input type="text" value={editedName} onChange={e => setEditedName(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') handleCancelEdit();
                }} className="text-3xl font-bold text-slate-900 border-2 border-orange-300 rounded-lg px-4 py-2 focus:border-orange-500 min-h-[56px] max-w-[200px]" autoFocus maxLength={30} fullWidth={false} data-id="element-1926" />
                    <button onClick={handleSaveName} className="p-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors min-h-[48px] min-w-[48px] focus:outline-none focus:ring-2 focus:ring-emerald-300 flex items-center justify-center" title="Opslaan" data-id="element-1927">
                      <Save className="w-5 h-5" data-id="element-1928" />
                    </button>
                    <button onClick={handleCancelEdit} className="p-3 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300 transition-colors min-h-[48px] min-w-[48px] focus:outline-none focus:ring-2 focus:ring-slate-300 flex items-center justify-center" title="Annuleren" data-id="element-1929">
                      <X className="w-5 h-5" data-id="element-1930" />
                    </button>
                  </div> : <div className="flex items-center gap-4 mb-3 justify-center sm:justify-start" data-id="element-1931">
                    <h2 className="text-3xl font-black text-slate-900" data-id="element-1932">
                      {displayName}
                    </h2>
                    {onUpdateName && <button onClick={() => setIsEditingName(true)} className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors min-h-[40px] min-w-[40px] focus:outline-none focus:ring-2 focus:ring-orange-300 flex items-center justify-center" title="Naam wijzigen" data-id="element-1933">
                        <Edit2 className="w-5 h-5" data-id="element-1934" />
                      </button>}
                  </div>}

                <div className="flex flex-wrap justify-center sm:justify-start gap-3 mt-3" data-id="element-1935">
                  <Badge variant="info" icon={<School className="w-4 h-4" data-id="element-1937" />} size="lg" data-id="element-1936">
                    Groep {selectedGroup}
                  </Badge>
                  <Badge variant="default" icon={<Shield className="w-4 h-4" data-id="element-1939" />} size="lg" data-id="element-1938">
                    {roleLabel}
                  </Badge>
                </div>

                {/* XP Progress */}
                <div className="mt-6 max-w-xs mx-auto sm:mx-0" data-id="element-1940">
                  <div className="flex items-center justify-between mb-2" data-id="element-1941">
                    <span className="text-sm font-semibold text-slate-600" data-id="element-1942">
                      Level {state.stats.level}
                    </span>
                    <span className="text-sm font-bold text-orange-600" data-id="element-1943">
                      {state.stats.xp} XP
                    </span>
                  </div>
                  <ProgressBar progress={getProgressToNextLevel(state.stats.xp).percent} color="orange" size="md" animated data-id="element-1944" />
                  <p className="text-xs text-slate-500 mt-2 text-center sm:text-left" data-id="element-1945">
                    Nog{' '}
                    {getProgressToNextLevel(state.stats.xp).needed - getProgressToNextLevel(state.stats.xp).current}{' '}
                    XP tot Level {state.stats.level + 1}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </WorldZone>
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-1946">
        <WorldZone title="Voorkeuren" icon={Sliders} accentColor="bg-amber-100 text-amber-600" isLast={false} data-id="element-1947">
          <div className="space-y-2" data-id="element-1948">
            {/* Sound Toggle */}
            <div className="flex items-center justify-between p-4 rounded-2xl hover:bg-white/50 transition-colors" data-id="element-1949">
              <div className="flex items-center gap-4" data-id="element-1950">
                <div className="w-11 h-11 rounded-xl bg-sky-50 flex items-center justify-center text-sky-600 shadow-sm border border-sky-100" data-id="element-1951">
                  <Volume2 className="w-5 h-5" data-id="element-1952" />
                </div>
                <div data-id="element-1953">
                  <div className="font-bold text-slate-900" data-id="element-1954">
                    Geluidseffecten
                  </div>
                  <div className="text-sm text-slate-600" data-id="element-1955">
                    Geluidjes bij goede antwoorden en beloningen
                  </div>
                </div>
              </div>
              <Toggle checked={prefs.sound} onChange={v => updatePref('sound', v)} label="Geluidseffecten" variant="student" data-id="element-1956" />
            </div>

            {/* Reduced Motion Toggle */}
            <div className="flex items-center justify-between p-4 rounded-2xl hover:bg-white/50 transition-colors" data-id="element-1957">
              <div className="flex items-center gap-4" data-id="element-1958">
                <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 shadow-sm border border-purple-100" data-id="element-1959">
                  <Sparkles className="w-5 h-5" data-id="element-1960" />
                </div>
                <div data-id="element-1961">
                  <div className="font-bold text-slate-900" data-id="element-1962">
                    Animaties verminderen
                  </div>
                  <div className="text-sm text-slate-600" data-id="element-1963">
                    Rustiger beeld zonder bewegende effecten
                  </div>
                  <div className="text-xs text-purple-600 font-medium mt-0.5" data-id="element-1964">
                    Wordt actief na herladen
                  </div>
                </div>
              </div>
              <Toggle checked={prefs.reducedMotion} onChange={v => updatePref('reducedMotion', v)} label="Animaties verminderen" variant="student" data-id="element-1965" />
            </div>

            {/* Daily Goal Selector */}
            <div className="p-4 rounded-2xl" data-id="element-1966">
              <div className="flex items-center gap-4 mb-5" data-id="element-1967">
                <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shadow-sm border border-amber-100" data-id="element-1968">
                  <Target className="w-5 h-5" data-id="element-1969" />
                </div>
                <div data-id="element-1970">
                  <div className="font-bold text-slate-900" data-id="element-1971">Dagelijks doel</div>
                  <div className="text-sm text-slate-600" data-id="element-1972">
                    Hoeveel vragen wil je per dag oefenen?
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-1973">
                {dailyGoalOptions.map(opt => <button key={opt.value} onClick={() => updatePref('dailyGoal', opt.value)} className={`
                      relative py-5 px-3 rounded-2xl border-2 font-bold transition-all text-center
                      ${prefs.dailyGoal === opt.value ? 'border-amber-400 bg-amber-50 text-amber-800 shadow-[0_2px_0_0_rgb(251,191,36)]' : 'border-white/60 bg-white/40 text-slate-600 hover:border-amber-200 hover:bg-amber-50/50'}
                    `} aria-pressed={prefs.dailyGoal === opt.value} data-id="element-1974">
                    <div className="text-2xl mb-1" data-id="element-1975">{opt.emoji}</div>
                    <div className="text-xl" data-id="element-1976">{opt.value}</div>
                    <div className="text-xs font-semibold text-slate-500 mt-0.5" data-id="element-1977">
                      {opt.label}
                    </div>
                  </button>)}
              </div>
            </div>

            {/* Weekly Goal Selector */}
            <div className="p-4 rounded-2xl" data-id="element-1978">
              <div className="flex items-center gap-4 mb-5" data-id="element-1979">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 shadow-sm border border-emerald-100" data-id="element-1980">
                  <Calendar className="w-5 h-5" data-id="element-1981" />
                </div>
                <div data-id="element-1982">
                  <div className="font-bold text-slate-900" data-id="element-1983">Weekdoel</div>
                  <div className="text-sm text-slate-600" data-id="element-1984">
                    Hoeveel dagen per week wil je oefenen?
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-1985">
                {weeklyGoalOptions.map(opt => <button key={opt.value} onClick={() => updatePref('weeklyGoal', opt.value)} className={`
                      relative py-5 px-3 rounded-2xl border-2 font-bold transition-all text-center
                      ${prefs.weeklyGoal === opt.value ? 'border-emerald-400 bg-emerald-50 text-emerald-800 shadow-[0_2px_0_0_rgb(52,211,153)]' : 'border-white/60 bg-white/40 text-slate-600 hover:border-emerald-200 hover:bg-emerald-50/50'}
                    `} aria-pressed={prefs.weeklyGoal === opt.value} data-id="element-1986">
                    <div className="text-2xl mb-1" data-id="element-1987">{opt.emoji}</div>
                    <div className="text-xl" data-id="element-1988">{opt.value}</div>
                    <div className="text-xs font-semibold text-slate-500 mt-0.5" data-id="element-1989">
                      {opt.label}
                    </div>
                  </button>)}
              </div>
            </div>
          </div>
        </WorldZone>
      </motion.div>

      {isLearner && <motion.div variants={itemVariants} data-id="element-1990">
          <WorldZone title="Instapmoment" icon={Calendar} accentColor="bg-teal-100 text-teal-600" isLast={false} data-id="element-1991">
            <div className="space-y-4" data-id="element-1992">
              <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-1993">
                <div className="flex items-start gap-4 mb-5" data-id="element-1994">
                  <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600 shadow-sm border border-teal-100 flex-shrink-0" data-id="element-1995">
                    <Calendar className="w-5 h-5" data-id="element-1996" />
                  </div>
                  <div data-id="element-1997">
                    <div className="font-bold text-slate-900" data-id="element-1998">
                      Kies je startmoment
                    </div>
                    <div className="text-sm text-slate-600" data-id="element-1999">
                      Bepaalt welke stof als eerste wordt aangeboden. Standaard
                      past dit zich automatisch aan het schooljaar aan.
                    </div>
                  </div>
                </div>

                {hasMasteryData && <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-id="element-2000">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-2001" />
                    <div className="text-sm text-amber-800" data-id="element-2002">
                      Je hebt al geoefend. Om je startmoment opnieuw in te
                      stellen, moet je eerst je voortgang wissen.
                      <button onClick={() => setResetConfirmOpen(true)} className="block mt-2 text-amber-600 font-bold hover:text-amber-700 underline" data-id="element-2003">
                        Voortgang wissen
                      </button>
                    </div>
                  </div>}

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-2004">
                  {periodOptions.map(opt => <button key={opt.value || 'auto'} onClick={() => onSetStartingPeriod(opt.value)} disabled={hasMasteryData} className={`
                        relative py-4 px-2 rounded-2xl border-2 font-bold transition-all text-center flex flex-col items-center justify-center
                        ${(manualPeriod ?? null) === opt.value ? 'border-teal-400 bg-teal-50 text-teal-800 shadow-[0_2px_0_0_rgb(45,212,191)]' : 'border-slate-200 bg-white text-slate-600 hover:border-teal-200 hover:bg-teal-50/50'}
                        ${hasMasteryData ? 'opacity-50 cursor-not-allowed' : ''}
                      `} aria-pressed={(manualPeriod ?? null) === opt.value} title={opt.fullLabel} data-id="element-2005">
                      <div className="text-2xl mb-1 flex items-center justify-center h-8" data-id="element-2006">
                        {opt.emoji}
                      </div>
                      <div className="text-sm font-semibold mt-1" data-id="element-2007">
                        {opt.label}
                      </div>
                    </button>)}
                </div>

                {(manualPeriod ?? null) === null && <div className="mt-4 text-sm text-center text-slate-500 font-medium" data-id="element-2008">
                    Huidig automatisch moment:{' '}
                    <span className="text-teal-600 font-bold" data-id="element-2009">
                      {getAutoPhaseLabel()}
                    </span>
                  </div>}
              </div>
            </div>
          </WorldZone>
        </motion.div>}

      {isLearner && <motion.div variants={itemVariants} data-id="element-2010">
          <WorldZone title="Koppelcode & Privacy" icon={Shield} accentColor="bg-blue-100 text-blue-600" isLast={false} data-id="element-2011">
            <div className="space-y-6" data-id="element-2012">
              {/* Klascode invoeren (only if no teacher linked) */}
              {linkedInfo && linkedInfo.teachers.length === 0 && !joinSuccess && <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2013">
                    <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-lg" data-id="element-2014">
                      <Users className="w-5 h-5 text-blue-500" data-id="element-2015" />
                      Klascode invoeren
                    </h3>
                    <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-2016">
                      Heb je een code gekregen van je leraar? Vul hem hier in om
                      je aan te sluiten bij de klas.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3" data-id="element-2017">
                      <Input type="text" value={joinCode} onChange={e => {
                setJoinCode(e.target.value.toUpperCase());
                setJoinError(null);
              }} placeholder="Bijv. A1B2C3" maxLength={6} className="bg-slate-50 border-slate-200 py-3 font-mono text-lg font-bold text-slate-900 focus:ring-blue-500 uppercase" data-id="element-2018" />
                      <Button onClick={handleJoinClassroom} disabled={joinCode.trim().length !== 6 || joinLoading} className="py-3 px-6" data-id="element-2019">
                        {joinLoading ? 'Koppelen...' : 'Koppelen'}
                      </Button>
                    </div>
                    {joinError && <p className="text-sm text-rose-500 mt-3 font-medium flex items-center gap-1.5" data-id="element-2020">
                        <AlertCircle className="w-4 h-4" data-id="element-2021" />
                        {joinError}
                      </p>}
                  </div>}

              {/* Success message after joining */}
              {joinSuccess && <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-100 shadow-sm flex items-center gap-4" data-id="element-2022">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-2023">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" data-id="element-2024" />
                  </div>
                  <div data-id="element-2025">
                    <h3 className="font-bold text-emerald-900 text-lg" data-id="element-2026">
                      Gekoppeld!
                    </h3>
                    <p className="text-emerald-700 font-medium" data-id="element-2027">
                      Je bent succesvol toegevoegd aan de klas.
                    </p>
                  </div>
                </div>}

              {/* Koppelcode */}
              {linkedInfo?.linkCode && <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2028">
                  <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-lg" data-id="element-2029">
                    <LinkIcon className="w-5 h-5 text-blue-500" data-id="element-2030" />
                    Mijn Koppelcode
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-2031">
                    Deel deze code met je ouder(s) zodat zij je voortgang kunnen
                    volgen.
                  </p>
                  <div className="flex items-center gap-3" data-id="element-2032">
                    <div className="bg-blue-50 border border-blue-100 px-5 py-2.5 rounded-xl font-mono text-xl font-black tracking-widest text-blue-900" data-id="element-2033">
                      {linkedInfo.linkCode}
                    </div>
                    <button onClick={handleCopyCode} className="p-2.5 bg-blue-50 border border-blue-100 rounded-xl text-blue-600 hover:bg-blue-100 transition-all" title="Kopieer code" data-id="element-2034">
                      {codeCopied ? <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-2035" /> : <Copy className="w-5 h-5" data-id="element-2036" />}
                    </button>
                    {codeCopied && <span className="text-sm font-bold text-emerald-600" data-id="element-2037">
                        Gekopieerd!
                      </span>}
                  </div>
                </div>}

              {/* Wie kan mijn voortgang zien? */}
              {linkedInfo && (linkedInfo.parents.length > 0 || linkedInfo.teachers.length > 0) && <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2038">
                    <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-lg" data-id="element-2039">
                      <Eye className="w-5 h-5 text-emerald-500" data-id="element-2040" />
                      Wie kan mijn voortgang zien?
                    </h3>
                    <p className="text-sm text-slate-600 mb-4 font-medium" data-id="element-2041">
                      Deze mensen kunnen je scores bekijken, maar niet je
                      wachtwoord of instellingen wijzigen.
                    </p>
                    <div className="space-y-4" data-id="element-2042">
                      {linkedInfo.parents.length > 0 && <div data-id="element-2043">
                          <div className="flex items-center gap-2 mb-2" data-id="element-2044">
                            <Heart className="w-4 h-4 text-rose-500" data-id="element-2045" />
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-2046">
                              Ouders / Verzorgers
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2" data-id="element-2047">
                            {linkedInfo.parents.map((p, i) => <div key={i} className="flex items-center gap-2 bg-rose-50 rounded-lg px-3 py-1.5 border border-rose-100" data-id="element-2048">
                                <span className="text-sm font-semibold text-rose-900" data-id="element-2049">
                                  {p.name}
                                </span>
                              </div>)}
                          </div>
                        </div>}
                      {linkedInfo.teachers.length > 0 && <div data-id="element-2050">
                          <div className="flex items-center gap-2 mb-2" data-id="element-2051">
                            <GraduationCap className="w-4 h-4 text-orange-500" data-id="element-2052" />
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-2053">
                              Docenten
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2" data-id="element-2054">
                            {linkedInfo.teachers.map((t, i) => <div key={i} className="flex items-center gap-2 bg-orange-50 rounded-lg px-3 py-1.5 border border-orange-100" data-id="element-2055">
                                <span className="text-sm font-semibold text-orange-900" data-id="element-2056">
                                  {t.name}
                                </span>
                                {t.className && <span className="text-xs text-orange-600" data-id="element-2057">
                                    ({t.className})
                                  </span>}
                              </div>)}
                          </div>
                        </div>}
                    </div>
                  </div>}
            </div>
          </WorldZone>
        </motion.div>}

      <motion.div variants={itemVariants} data-id="element-2058">
        <WorldZone title="Account" icon={LogOut} accentColor="bg-slate-100 text-slate-600" isLast={true} data-id="element-2059">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4" data-id="element-2060">
            <div data-id="element-2061">
              <h3 className="text-lg font-black text-slate-800" data-id="element-2062">
                Sessie beëindigen
              </h3>
              <p className="text-slate-600 font-medium" data-id="element-2063">
                Wissel van gebruiker of sluit de sessie af.
              </p>
            </div>
            <Button onClick={onLogout} variant="secondary" className="w-full sm:w-auto text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-900" data-id="element-2064">
              <LogOut className="w-5 h-5 mr-2" data-id="element-2065" />
              Uitloggen
            </Button>
          </div>
        </WorldZone>
      </motion.div>

      <ConfirmDialog open={resetConfirmOpen} title="Voortgang wissen" message="Weet je zeker dat je alle voortgang wilt wissen? Dit kan niet ongedaan worden gemaakt." confirmLabel="Voortgang wissen" cancelLabel="Annuleren" variant="danger" onConfirm={confirmResetProgress} onCancel={() => setResetConfirmOpen(false)} data-id="element-2066" />
    </PageContainer>;
}